import type { ComponentPropsWithoutRef } from 'react';

import { clsxm } from '@vs-shop/utils';
import { type VariantProps, cva } from 'class-variance-authority';

/**
 * PriceTag — a structural price stack: an (optional) struck-through original
 * price above the current price. The compound stays domain-agnostic: amounts
 * arrive pre-formatted as `children`; discount rules, currency, and copy live
 * with the consumer.
 */
const priceTagVariants = cva('flex h-full flex-col justify-end gap-1', {
  variants: {
    align: {
      start: '',
      center: 'items-center',
    },
  },
  defaultVariants: { align: 'start' },
});

export type PriceTagProps = ComponentPropsWithoutRef<'div'> & VariantProps<typeof priceTagVariants>;

export function PriceTag({ align, className, ...props }: PriceTagProps) {
  return (
    <div
      data-slot="price-tag"
      {...props}
      className={clsxm(priceTagVariants({ align }), className)}
    />
  );
}

/**
 * The original-price row. Always rendered — even with no value inside it keeps
 * its place as a flex child, so the column's gap spacing (and thus the current
 * price's vertical position) is stable whether or not a discount is shown.
 */
export type PriceTagOriginalProps = ComponentPropsWithoutRef<'div'>;

export function PriceTagOriginal(props: PriceTagOriginalProps) {
  return <div data-slot="price-tag-original" {...props} />;
}

const priceTagOriginalValueVariants = cva('text-foreground-muted line-through', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-base/4',
    },
  },
  defaultVariants: { size: 'md' },
});

export type PriceTagOriginalValueProps = ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof priceTagOriginalValueVariants>;

export function PriceTagOriginalValue({ size, className, ...props }: PriceTagOriginalValueProps) {
  return (
    <div
      data-slot="price-tag-original-value"
      {...props}
      className={clsxm(priceTagOriginalValueVariants({ size }), className)}
    />
  );
}

export type PriceTagCurrentProps = ComponentPropsWithoutRef<'div'>;

export function PriceTagCurrent({ className, ...props }: PriceTagCurrentProps) {
  return (
    <div
      data-slot="price-tag-current"
      {...props}
      className={clsxm('flex text-[22px]/[22px] font-medium', className)}
    />
  );
}

export { priceTagVariants, priceTagOriginalValueVariants };
